import React from 'react';
import SubpageHero from '../components/SubpageHero';
import { useOverOnsHero } from '../hooks/useOverOnsHero';
import { useOverOnsAboutContent } from '../hooks/useOverOnsAboutContent';
import { Safe, Caduceus, Injection } from '../components/icons';
import LocationSection from '../components/LocationSection';
import TeamSection from '../components/TeamSection';
import UspSection from '../components/UspSection';

// Icon mapping for USPs
const iconMap = {
  SafeIcon: Safe,
  CaduceusIcon: Caduceus,
  InjectionIcon: Injection
};

// Custom USPs component
const USPsSection: React.FC<{ usp1: any; usp2: any; usp3: any }> = ({ usp1, usp2, usp3 }) => {
  const features = [usp1, usp2, usp3];

  return (
    <section style={{ 
      background: '#f8f8f8', 
      padding: '120px 120px',
      textAlign: 'center'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '48px',
          marginTop: '80px'
        }}>
          {features.filter(Boolean).map((feature, index) => {
            // Ensure feature exists and has an icon property
            if (!feature || !feature.icon) {
              console.warn('Feature missing or has no icon:', feature);
              return null;
            }
            
            const IconComponent = iconMap[feature.icon as keyof typeof iconMap] || Safe; // Fallback to Safe icon
            return (
              <div key={index} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '24px'
              }}>
                <div style={{
                  width: '84px',
                  height: '84px',
                  borderRadius: '50%',
                  background: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <IconComponent 
                    size={42} 
                    className="text-dark-blue"
                  />
                </div>
                <h3 style={{
                  fontFamily: 'Noto Sans',
                  fontWeight: '600',
                  fontStyle: 'Regular',
                  fontSize: '24px',
                  lineHeight: '120%',
                  letterSpacing: '0%',
                  color: '#02323A',
                  margin: '0'
                }}>
                  {feature.title}
                </h3>
                <p style={{
                  fontFamily: 'Noto Sans',
                  fontWeight: '400',
                  fontStyle: 'Regular',
                  fontSize: '16px',
                  lineHeight: '150%',
                  letterSpacing: '0%',
                  color: '#666666',
                  margin: '0'
                }}>
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const OverOns: React.FC = () => {
  const { data: heroData, loading: heroLoading, error: heroError } = useOverOnsHero();
  const { data: contentData, loading: contentLoading, error: contentError } = useOverOnsAboutContent();

  // Default content when no Strapi data is available
  const defaultContent = {
    title: "Gespecialiseerd in haarverlies en hoofdhuidproblemen bij mannen én vrouwen",
    description: "Bij HairMedics geloven we dat haarverlies invloed heeft op hoe je je voelt. Daarom combineren we medische kennis met persoonlijke aandacht, voor mannen en vrouwen die klaar zijn met vage beloftes en iets willen dat écht werkt.",
    usp1: {
      title: "Medisch verantwoord",
      description: "Behandelingen op basis van wetenschap en trichologie.",
      icon: "SafeIcon"
    },
    usp2: {
      title: "Volledige discretie",
      description: "Geen wachtruimte, volle privacy, behandeling in rust.",
      icon: "CaduceusIcon"
    },
    usp3: {
      title: "Toegankelijke methode",
      description: "Zonder operatie, past in je agenda en je leven.",
      icon: "InjectionIcon"
    }
  };

  if (heroLoading || contentLoading) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        fontSize: '18px'
      }}>
        Loading over-ons page...
      </div>
    );
  }

  if (heroError || contentError) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: 'red',
        fontSize: '18px'
      }}>
        Error loading page
      </div>
    );
  }

  // Use Strapi data if available, otherwise use default data (now flat structure)
  const content = contentData || defaultContent;

  // If no hero data is available, show a default hero
  if (!heroData) {
    return (
      <div>
        <div className="fallback-hero" style={{
          height: '560px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-start',
          padding: '0 120px 80px 120px'
        }}>
          <h1 style={{
            fontFamily: 'var(--h1-font-family)',
            fontWeight: 'var(--h1-font-weight)',
            fontStyle: 'var(--h1-font-style)',
            fontSize: 'var(--h1-font-size)',
            lineHeight: 'var(--h1-line-height)',
            letterSpacing: 'var(--h1-letter-spacing)',
            color: 'white',
            margin: '0'
          }}>
            Over Ons
          </h1>
        </div>
        
        {/* USP Section from Strapi */}
        <UspSection 
          useStrapi={true}
        />
        
        <LocationSection />
        <TeamSection />
      </div>
    );
  }

  // Safety check to ensure heroData exists and has title
  if (!heroData || !heroData.title) {
    console.error('Hero data is missing or malformed:', heroData);
    return (
      <div>
        <div className="fallback-hero" style={{
          height: '560px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '24px'
        }}>
          Over Ons - Default Hero
        </div>
        <LocationSection />
        <TeamSection />
      </div>
    );
  }

  return (
    <div>
      {(() => {
        const raw: any = heroData;
        // Support both backgroundImage shapes: {data.attributes.url} or flat {url}
        const bgFromData = raw?.backgroundImage?.data?.attributes;
        const bgFlat = raw?.backgroundImage;
        const bg = bgFromData || bgFlat;
        const backgroundMedia = bg
          ? { url: bg.url, alternativeText: bg.alternativeText }
          : undefined;
        const mappedTextColor = raw?.textColor === 'dark' ? 'black' : raw?.textColor;

        return (
          <SubpageHero
            title={raw.title}
            backgroundMedia={backgroundMedia}
            mediaType={raw.mediaType}
            videoUrl={raw.videoUrl}
            overlayOpacity={raw.overlayOpacity}
            textColor={mappedTextColor}
            paddingBottom={raw.paddingBottom}
            paddingLeft={raw.paddingLeft}
            paddingRight={raw.paddingRight}
          />
        );
      })()}
      
      {/* USP Section from Strapi */}
      <UspSection useStrapi={true} />
      
      <LocationSection />
      <TeamSection />
    </div>
  );
};

export default OverOns; 